import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { ProjectionProviderType } from "@app/projects/enumerators";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { ProviderProfileActionEnum, ServicingAccessPoint } from "@app/projects/network-assessment/provider-hub/interfaces";
import { ProjectionPanelMode, ProjectionProviderDetails } from "@app/projects/projection-provider";
import projectionProviderDetailsReducer, { projectionProviderDetailsInitialState, ProjectionProviderIdentifier } from "@app/projects/projection-provider/projectionProviderDetailsReducer";
import { AccessPointForProjection } from "@app/projects/results/projection-provider";
import { TerminatePanelMode, WrappedTerminateProvidersPanel } from "@app/projects/servicing-provider/TerminateProvidersPanel";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { FC, useCallback, useContext, useReducer } from "react";
import { showProviderHubActionToast } from "@app/projects/network-assessment/provider-hub/utility";

interface ContractEntityProfileActionFlyoutProps {
  accessPoint: ServicingAccessPoint;
  providerAction: ProviderProfileActionEnum;
  allAccessPoints: boolean;
  individualAccessPoint?: boolean;
  onSubmit?: () => void;
  excludedAccessPointsCount?: number;
  targetedAccessPointsCount?: number;
}

const ContractEntityProfileActionFlyout: FC<ContractEntityProfileActionFlyoutProps> = ({
  accessPoint,
  providerAction,
  allAccessPoints,
  onSubmit,
  individualAccessPoint,
  excludedAccessPointsCount = 0,
  targetedAccessPointsCount = 0
}) => {
  const projectContext = useContext(CountyProjectContext);
  const providerHubContext = useContext(ProviderHubContext);

  const [projectionProvidersState, projectionProvidersReducer] = useReducer(projectionProviderDetailsReducer, projectionProviderDetailsInitialState);

  const { selectedContractEntity } = providerHubContext;

  const getProviderActionTypeDisplayName = (type: ProviderProfileActionEnum) => {
    switch (type) {
      case ProviderProfileActionEnum.Target:
        return "Targeting";
      case ProviderProfileActionEnum.Exclude:
        return "Excluding";
      case ProviderProfileActionEnum.Terminate:
        return "Terminating";
      case ProviderProfileActionEnum.Unknown:
        return "Unknown";
    }
  };

  const getProjectionProviderType = (type: ProviderProfileActionEnum): ProjectionProviderType => {
    switch (type) {
      case ProviderProfileActionEnum.Target:
        return ProjectionProviderType.Target;
      case ProviderProfileActionEnum.Exclude:
        return ProjectionProviderType.ExcludeImpact;
      default:
        return ProjectionProviderType.Unknown;
    }
  }

  const accessPointsForAction: AccessPointForProjection[] = [{
    npi: accessPoint.npi,
    professionalAffiliationId: selectedContractEntity.contractEntityId,
    projectionProviderType: ProjectionProviderType.Target,
    providerId: accessPoint.providerId,
    providerNetworkType: accessPoint.providerNetworkType,
    specialtyGroupCode: accessPoint.specialtyGroupCode,
    tin: accessPoint.tin,
  } as AccessPointForProjection];

  const onProjectionProvidersCreated = useCallback(async (runProjection: boolean, hasUnprocessedProviders = true) => {
    if (runProjection) {
      await projectContext.processProviderChanges(false);
    } else {
      projectContext.setProjectHasUnprocessedProjectionProviders(hasUnprocessedProviders, false, getProjectionProviderType(providerAction));
    }

    showProviderHubActionToast("Contract Entity", providerAction, !runProjection);
    onSubmit();
  }, [projectContext, onSubmit, providerAction]);

  const hasExcludedAccessPoints = excludedAccessPointsCount > 0;
  const hasTargetedAccessPoints = targetedAccessPointsCount > 0;

  const renderActionAlertOrInfoBox = () => {
    if (providerAction === ProviderProfileActionEnum.Target && hasExcludedAccessPoints) {
      return (
        <QESAlert type="warning" persist>
          <p className="demibold mt-2 mb-2">Some access points are currently excluded. Target actions will only apply to access points without an action status. To target excluded access points, remove the Exclusion.</p>
        </QESAlert>
      );
    }

    if (providerAction === ProviderProfileActionEnum.Exclude && hasTargetedAccessPoints) {
      return (
        <QESAlert type="warning" persist>
          <p className="demibold mt-2 mb-2">Some access points have been targeted. Applying the Exclusion will override the existing Target rule.</p>
        </QESAlert>
      );
    }

    return (
      <div className="provider-profile-target-flyout__info-box">
        {allAccessPoints ?
          <p>{selectedContractEntity.name ?? "---"}: All Specialties, All Locations</p> :
          <p>{getProviderActionTypeDisplayName(providerAction)} will apply to all applicable {providerAction === ProviderProfileActionEnum.Terminate ? 'network' : 'out-of-network'} access points</p>
        }
      </div>
    );
  };

  return (
    <div>
      <p>Please enter the necessary information to {providerAction.toLocaleLowerCase()} the available providers for your network.</p>

      {renderActionAlertOrInfoBox()}

      {providerAction === ProviderProfileActionEnum.Terminate ? (
        <WrappedTerminateProvidersPanel
          projectId={projectContext.project.projectId}
          onSubmit={() => {
            projectContext.setProjectHasUnprocessedTerminations(true);
            showProviderHubActionToast("Contract Entity", ProviderProfileActionEnum.Terminate, true);
            onSubmit();
          }}
          crossSpecialtyAndNetwork={individualAccessPoint ? null : {
            allLocations: true,
            allProjects: false,
            allSpecialties: true,
            projectionBy: ProjectionProviderIdentifier.ContractEntity,
          }}
          terminatePanelMode={TerminatePanelMode.Create}
          selectedProviders={accessPointsForAction}
          showAffiliationsInsights={projectContext.project.showAffiliationsInsights}
          hideAllLocationsOption
          hideAllSpecialtiesOption
        />
      ) : (
        <ProjectionProviderDetails
          accessPoints={accessPointsForAction}
          crossSpecialtyAndNetworkType={individualAccessPoint ? null :{
            allLocations: true,
            allProjects: false,
            allSpecialties: true,
            projectionBy: ProjectionProviderIdentifier.ContractEntity,
          }}
          showAffiliationsInsights={projectContext.project.showAffiliationsInsights}
          formState={projectionProvidersState}
          formDispatch={projectionProvidersReducer}
          hasProvidersWithoutNPI={false} // TODO: how calculate?
          networkAssessmentAnalysisType={projectContext.runConfiguration.networkAssessmentAnalysisType}
          projectId={projectContext.project.projectId}
          projectionPanelMode={ProjectionPanelMode.Create}
          projectionProviderType={getProjectionProviderType(providerAction)}
          providerNetworkType={selectedContractEntity.providerNetworkTypes[0]}
          specialtyGroupCode=""
          isZipProject={false}
          isRunning={projectContext.project?.isRunning}
          hideProviderInfoSection
          onProjectionProvidersCreated={onProjectionProvidersCreated}
          hideAllLocationsOption
          hideAllSpecialtiesOption
          hideAllProjectsOption={!individualAccessPoint}
        />
      )}
    </div>
  );
}

export default ContractEntityProfileActionFlyout;